import React, { useState, useEffect, useMemo } from "react";
import {
    Container,
    Card,
    CardBody,
    CardTitle,
    Button,
    Table,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
} from "reactstrap";
import Session from "../auth/Session";
import CustomerForm from "./CustomerForm";
import useSortableData from "./useSortableData";
import SortableHeader from "./SortableHeader";

const CustomerGrid = () => {
    const apiBaseUrl = "/api/customers";
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const sortGetters = useMemo(() => ({
        salesRep: (c) => c.salesRep?.userName || c.salesRepId,
    }), []);

    const { items: sortedCustomers, sortConfig, requestSort } = useSortableData(
        customers,
        { key: "name", direction: "asc", getters: sortGetters }
    );

    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const token = await Session.getToken();
            const res = await fetch(apiBaseUrl, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error("Failed to load customers");
            const data = await res.json();
            setCustomers(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, []);

    const handleNewCustomer = () => {
        setEditingCustomer(null);
        setModalOpen(true);
    };

    const handleEditCustomer = (customer) => {
        setEditingCustomer(customer);
        setModalOpen(true);
    };

    const handleSuccess = () => {
        setModalOpen(false);
        fetchCustomers();
    };

    return (
        <Container className="mt-5">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle tag="h5">Customer Management</CardTitle>
                        <Button color="primary" onClick={handleNewCustomer}>
                            + New Customer
                        </Button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                        </div>
                    ) : (
                        <Table bordered hover responsive>
                            <thead className="table-light">
                                <tr>
                                    <SortableHeader label="Name" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Contact" sortKey="contactName" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Email" sortKey="contactEmail" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="City" sortKey="city" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="State" sortKey="state" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Sales Rep" sortKey="salesRep" sortConfig={sortConfig} onSort={requestSort} />
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedCustomers.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center">
                                            No customers found.
                                        </td>
                                    </tr>
                                ) : (
                                    sortedCustomers.map((c) => (
                                        <tr key={c.id}>
                                            <td>{c.name}</td>
                                            <td>{c.contactName}</td>
                                            <td>{c.contactEmail}</td>
                                            <td>{c.city}</td>
                                            <td>{c.state}</td>
                                            <td>{c.salesRep?.userName || c.salesRepId}</td>
                                            <td className="text-end">
                                                <Button
                                                    size="sm"
                                                    color="info"
                                                    onClick={() => handleEditCustomer(c)}
                                                >
                                                    Edit
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
                <ModalHeader toggle={() => setModalOpen(false)}>
                    {editingCustomer ? "Edit Customer" : "New Customer"}
                </ModalHeader>
                <ModalBody>
                    <CustomerForm
                        customer={editingCustomer}
                        apiBaseUrl={apiBaseUrl}
                        onSuccess={handleSuccess}
                        onCancel={() => setModalOpen(false)}
                    />
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default CustomerGrid;
